'use client'

import { PlusIcon } from 'lucide-react'
import React, { useId, useState } from 'react'

import { cn } from './cn'

interface AccordionProps {
  title?: string
  children?: React.ReactNode
  defaultOpen?: boolean
}

export const Accordion = ({
  title,
  children,
  defaultOpen = false,
}: AccordionProps): React.ReactElement => {
  const [open, setOpen] = useState(defaultOpen)
  const contentId = useId()

  return (
    <div
      className={cn(
        'snplay-can-focus-within snplay:rounded-lg snplay:border',
        'snplay:transition snplay:hover:shadow-sm',
        'snplay:border-[rgb(var(--snplay-ui-decorator-medium-rgb))] snplay:hover:border-ui-primary',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={(): void => setOpen(!open)}
        className={cn(
          'snplay:flex snplay:w-full snplay:cursor-pointer snplay:items-start snplay:gap-4',
          'snplay:p-6 snplay:text-left snplay:text-base',
          'snplay:font-bold snplay:transition snplay:outline-none',
          'snplay:hover:text-primaryDark',
          open && 'snplay:text-primaryDark',
        )}
      >
        <span className="snplay:block snplay:flex-1">{title}</span>
        <PlusIcon
          className={cn(
            'snplay:my-0.5 snplay:size-6',
            'snplay:shrink-0 snplay:transition-transform',
            open && 'snplay:rotate-45',
          )}
        />
      </button>
      <div
        id={contentId}
        inert={!open}
        className={cn(
          'snplay:grid snplay:transition-[grid-template-rows]',
          open ? 'snplay:grid-rows-[1fr]' : 'snplay:grid-rows-[0fr]',
        )}
      >
        <div className="snplay:overflow-hidden">
          <div
            className={cn(
              'snplay:space-y-2 snplay:p-6 snplay:pt-0',
              'snplay:text-base snplay:text-ui-secondary',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
